import type { EvoLinkCategory, EvoLinkPackageDraft, ExportScope, FilmProject, Scene, SceneChunk, StoryboardFrame } from "../types";

export const DEFAULT_PROVIDER_CHUNK_SECONDS = 10;

export const evoLinkCategories: Array<{ id: EvoLinkCategory; label: string; description: string }> = [
  { id: "boards", label: "Boards", description: "Selected, enabled frames and prompts" },
  { id: "characters", label: "Characters", description: "Uploaded/generated cast references" },
  { id: "voices", label: "Voices", description: "User audio or generated voice presets" },
  { id: "lines", label: "Lines", description: "Spoken lines and narration only" },
  { id: "locations", label: "Locations", description: "Scene and set references" },
  { id: "music", label: "Music", description: "Optional score/background music" },
  { id: "sfx", label: "SFX", description: "Atmosphere, foley, and scene sound" },
  { id: "style", label: "Cinematic style", description: "Lens language, movement, and visual preset" },
  { id: "colorGrade", label: "Color grade", description: "Grade, contrast, and palette intent" },
  { id: "aspect", label: "Aspect ratio", description: "Frame shape, letterbox, and crop safety" },
  { id: "chunkTiming", label: "Chunk timing", description: "Runtime/chunk timing for provider clips" }
];

export function frameStatus(frame: StoryboardFrame): string {
  if (frame.disabled) return "Disabled";
  if (frame.locked && frame.source === "uploaded") return "Uploaded · Locked";
  if (frame.generationStatus === "uploaded" || frame.source === "uploaded") return "Uploaded";
  if (frame.locked) return "Locked";
  if (frame.generationStatus === "failed") return "Failed";
  if (frame.generationStatus === "generating") return "Generating";
  if (frame.generationStatus === "queued" || frame.queuedRegenerate) return "Queued Regenerate";
  if (frame.generationStatus === "generated" || frame.source === "generated") return "Generated";
  if (frame.generationStatus === "saved") return "Saved";
  if (frame.generationStatus === "prompt_ready") return "Prompt Ready";
  if (frame.source === "internal") return "Placeholder";
  return "Current";
}

export function frameSourceLabel(frame: StoryboardFrame): "Uploaded" | "Generated" | "Parsed" | "Placeholder" | "Library" {
  const source = String(frame.source || "").toLowerCase();
  const status = String(frame.generationStatus || "").toLowerCase();
  if (source === "uploaded" || status === "uploaded") return "Uploaded";
  if (source === "generated" || status === "generated") return "Generated";
  if (source === "parsed" || frame.fileName?.toLowerCase().includes("reference")) return "Parsed";
  if (source === "current" || source === "library" || status === "selected" || Boolean(frame.sourceUrl || frame.storageKey)) return "Library";
  return "Placeholder";
}

export function frameSourceClass(frame: StoryboardFrame): string {
  return frameSourceLabel(frame).toLowerCase();
}

export function storyboardFrameIsReady(frame: StoryboardFrame): boolean {
  if (frame.disabled) return false;
  if (frame.generationStatus === "failed" || frame.generationStatus === "queued" || frame.generationStatus === "generating") return false;
  if (frame.queuedRegenerate) return false;
  if (frame.locked) return true;
  if (frame.source === "uploaded" || frame.source === "generated") return true;
  return frame.generationStatus === "uploaded" || frame.generationStatus === "generated" || frame.generationStatus === "saved";
}

export function storyboardFrameNeedsGeneration(frame: StoryboardFrame): boolean {
  if (frame.disabled) return false;
  return !storyboardFrameIsReady(frame);
}

export function sceneStoryboardIssue(scene: Scene): string {
  const enabledFrames = scene.frames.filter((frame) => !frame.disabled);
  if (!enabledFrames.length) return "No enabled boards";
  const failed = enabledFrames.filter((frame) => frame.generationStatus === "failed").length;
  if (failed) return `${failed} board${failed === 1 ? "" : "s"} need retry`;
  const inFlight = enabledFrames.filter((frame) => frame.generationStatus === "queued" || frame.generationStatus === "generating" || frame.queuedRegenerate).length;
  if (inFlight) return `${inFlight} board${inFlight === 1 ? "" : "s"} queued`;
  const open = enabledFrames.filter(storyboardFrameNeedsGeneration).length;
  if (open) return `${open} board${open === 1 ? "" : "s"} need generation`;
  return "";
}

export function sceneStoryboardReadyCount(scene: Scene): number {
  return scene.frames.filter(storyboardFrameIsReady).length;
}

export function sceneStatus(scene: Scene): string {
  if (scene.readiness === "ready") return "Ready";
  if (scene.readiness === "needs_character") return "Needs character";
  if (scene.readiness === "needs_voice") return "Needs voice";
  return "Missing reference";
}

export function scenesForScope(project: FilmProject, selectedSceneId: string, scope: ExportScope): Scene[] {
  if (scope === "all") return project.scenes;
  if (scope === "beats") {
    const beatSceneIds = new Set(project.agentBeats.map((beat) => beat.sceneId));
    return project.scenes.filter((scene) => beatSceneIds.has(scene.id));
  }
  if (scope === "selected") {
    return project.scenes.filter((scene) => scene.readiness === "ready" || scene.id === selectedSceneId);
  }
  return project.scenes.filter((scene) => scene.id === selectedSceneId);
}

export function framesForScope(project: FilmProject, selectedSceneId: string, scope: ExportScope): StoryboardFrame[] {
  return scenesForScope(project, selectedSceneId, scope).flatMap((scene) => scene.frames);
}

function clampDuration(seconds: number, fallback = DEFAULT_PROVIDER_CHUNK_SECONDS): number {
  const safe = Math.round(Number(seconds));
  return Number.isFinite(safe) && safe > 0 ? safe : fallback;
}

export function buildSceneChunks(scene: Scene, chunkSeconds = DEFAULT_PROVIDER_CHUNK_SECONDS): SceneChunk[] {
  const totalSeconds = clampDuration(scene.durationSeconds, chunkSeconds);
  const count = Math.max(1, Math.ceil(totalSeconds / chunkSeconds));
  const enabledFrames = scene.frames.filter((frame) => !frame.disabled);
  const framesPerChunk = Math.max(1, Math.ceil(Math.max(1, enabledFrames.length) / count));
  let remaining = totalSeconds;
  return Array.from({ length: count }, (_, index) => {
    const durationSeconds = index === count - 1 ? remaining : Math.min(chunkSeconds, remaining);
    remaining = Math.max(0, remaining - durationSeconds);
    const frameIds = enabledFrames.slice(index * framesPerChunk, (index + 1) * framesPerChunk).map((frame) => frame.id);
    return {
      id: `${scene.id}-chunk-${String(index + 1).padStart(2, "0")}`,
      sceneId: scene.id,
      index: index + 1,
      label: `Chunk ${index + 1}`,
      durationSeconds: clampDuration(durationSeconds, chunkSeconds),
      frameIds,
      enabled: true,
      locked: false
    };
  });
}

export function sceneChunks(scene: Scene): SceneChunk[] {
  const chunks = Array.isArray(scene.chunks) && scene.chunks.length ? scene.chunks : buildSceneChunks(scene);
  return [...chunks]
    .sort((left, right) => left.index - right.index)
    .map((chunk, index) => ({
      id: chunk.id || `${scene.id}-chunk-${String(index + 1).padStart(2, "0")}`,
      sceneId: chunk.sceneId || scene.id,
      index: index + 1,
      label: chunk.label || `Chunk ${index + 1}`,
      durationSeconds: clampDuration(chunk.durationSeconds),
      frameIds: Array.isArray(chunk.frameIds) ? chunk.frameIds : [],
      enabled: chunk.enabled !== false,
      locked: Boolean(chunk.locked)
    }));
}

export function enabledSceneChunks(scene: Scene): SceneChunk[] {
  return sceneChunks(scene).filter((chunk) => chunk.enabled);
}

export function chunkForFrame(scene: Scene, frame: StoryboardFrame): SceneChunk | undefined {
  return sceneChunks(scene).find((chunk) => chunk.frameIds.includes(frame.id));
}

export function frameBlockedByDisabledChunk(scene: Scene, frame: StoryboardFrame): boolean {
  const chunks = sceneChunks(scene);
  const disabledFrameIds = new Set(chunks.filter((chunk) => !chunk.enabled).flatMap((chunk) => chunk.frameIds));
  if (!disabledFrameIds.has(frame.id)) return false;
  const enabledFrameIds = new Set(chunks.filter((chunk) => chunk.enabled).flatMap((chunk) => chunk.frameIds));
  return !enabledFrameIds.has(frame.id);
}

export function chunkRuntimeSeconds(chunks: SceneChunk[]): number {
  return chunks.reduce((total, chunk) => total + (chunk.enabled ? clampDuration(chunk.durationSeconds) : 0), 0);
}

export function formatSceneChunking(scene: Scene): string {
  const chunks = enabledSceneChunks(scene);
  if (!chunks.length) return "0 chunks";
  const uniformDuration = chunks[0]?.durationSeconds || DEFAULT_PROVIDER_CHUNK_SECONDS;
  const uniform = chunks.every((chunk) => chunk.durationSeconds === uniformDuration);
  return uniform ? `${chunks.length} x ${uniformDuration}s` : `${chunks.length} chunks`;
}

export function missingItemsForScenes(scenes: Scene[], options: { includeBoards?: boolean } = {}): string[] {
  return scenes.flatMap((scene) => {
    const items: string[] = [];
    if (scene.readiness === "needs_character") items.push(`${scene.slugline}: character card`);
    if (scene.readiness === "needs_voice") items.push(`${scene.slugline}: voice`);
    if (scene.readiness === "missing_reference") items.push(`${scene.slugline}: scene reference`);
    if (options.includeBoards) {
      const storyboardIssue = sceneStoryboardIssue(scene);
      if (storyboardIssue) items.push(`${scene.slugline}: storyboard boards (${storyboardIssue})`);
    }
    return items;
  });
}

export function createPackageDraft(
  project: FilmProject,
  selectedSceneId: string,
  scope: ExportScope,
  categories: EvoLinkCategory[]
): EvoLinkPackageDraft {
  const scenes = scenesForScope(project, selectedSceneId, scope);
  const sceneIds = new Set(scenes.map((scene) => scene.id));
  const sceneCharacterNames = new Set(scenes.flatMap((scene) => scene.characters));
  const frameRows = scenes.flatMap((scene) => scene.frames.map((frame) => ({ scene, frame })));
  const frames = frameRows.map(({ frame }) => frame);
  const chunks = scenes.flatMap((scene) => sceneChunks(scene));
  const enabledChunks = chunks.filter((chunk) => chunk.enabled);
  const enabledFrames = categories.includes("boards")
    ? frameRows
        .filter(({ scene, frame }) => !frame.disabled && !frameBlockedByDisabledChunk(scene, frame))
        .map(({ frame }) => frame)
    : [];
  const characters = project.characters.filter((character) => sceneCharacterNames.has(character.name) && !character.cardDisabled);
  const voices = characters.filter((character) => character.voiceStatus !== "missing" && character.voiceStatus !== "not_needed" && !character.voiceDisabled);
  const references = (project.uploadedAssets?.sceneReferences || []).filter((asset) => asset.sceneId && sceneIds.has(asset.sceneId) && !asset.disabled);
  const runtimeSeconds = enabledChunks.length
    ? chunkRuntimeSeconds(enabledChunks)
    : scenes.reduce((total, scene) => total + scene.durationSeconds, 0);
  return {
    scope,
    sceneIds: scenes.map((scene) => scene.id),
    categories,
    chunks: enabledChunks,
    frameCount: enabledFrames.length,
    disabledFrameCount: frames.length - enabledFrames.length,
    disabledChunkCount: chunks.length - enabledChunks.length,
    lockedFrameCount: enabledFrames.filter((frame) => frame.locked).length,
    uploadedFrameCount: enabledFrames.filter((frame) => frame.source === "uploaded").length,
    characterCount: categories.includes("characters") ? characters.length : 0,
    voiceCount: categories.includes("voices") ? voices.length : 0,
    referenceCount: categories.includes("locations") ? references.length : 0,
    customFrameAssignmentCount: frames.filter((frame) => Boolean(frame.sourceAssignments)).length,
    runtimeSeconds,
    chunkCount: enabledChunks.length || Math.max(1, Math.ceil(runtimeSeconds / DEFAULT_PROVIDER_CHUNK_SECONDS)),
    output: `${project.resolution} · ${project.aspectRatio}`,
    dryRunCost: "$0.00 — dry run",
    missingItems: missingItemsForScenes(scenes, { includeBoards: categories.includes("boards") })
  };
}

export function formatRuntime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function clampRuntimeSeconds(value: number, fallback = 60): number {
  const seconds = Math.round(Number(value));
  return Number.isFinite(seconds) && seconds > 0 ? seconds : fallback;
}

export function chunkCountForSeconds(seconds: number, chunkSeconds = 10): number {
  return Math.max(1, Math.ceil(seconds / chunkSeconds));
}

export function formatChunking(seconds: number, chunkSeconds = 10): string {
  return `${chunkCountForSeconds(seconds, chunkSeconds)} x ${chunkSeconds}s`;
}
